import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowCounterClockwise, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { AppHeader } from "@/components/observer/AppHeader";
import { InstallBanner } from "@/components/observer/InstallPrompt";
import { ParticipantsEditor } from "@/components/observer/ParticipantsEditor";
import { CsvInput } from "@/components/observer/CsvInput";
import { SourcePicker } from "@/components/observer/SourcePicker";
import { QuickStart } from "@/components/observer/QuickStart";
import { BriefingLibrary } from "@/components/observer/BriefingLibrary";
import { FocusField } from "@/components/observer/FocusField";
import { api, errMsg } from "@/lib/api";
import { useUser } from "@/lib/user";
import { SITUATIONS } from "@/lib/situations";
import { extractAudio, needsExtraction } from "@/lib/extractAudio";
import { requestMeetingAudio, setPendingMeetingAudio, stopStream } from "@/lib/meetingAudio";
import { PrivateNote, PrivateToggle } from "@/components/observer/PrivateMeeting";
import { fmtDate } from "@/components/admin/format";
import { btnPrimary, inputCls, labelCls } from "@/lib/ui";

const EMPTY = { title: "", purpose: "", participants: [{ name: "", role: "", owns: "" }], briefing_csv: "", flag_interval_min: 10, chime: true, provider: "anthropic", private: false };
// The way this person likes to start: kept on their account, so it is the same on every device.
const PREF_KEYS = ["provider", "reasoning_effort", "chime", "flag_interval_min", "self_name"];

const readDraft = (key) => {
  try {
    localStorage.removeItem("observer.setupDraft"); // before accounts, one draft was shared by everyone on the browser
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};
const readLegacyPlace = () => {
  try {
    return localStorage.getItem("observer.quickPlace") === "online" ? "online" : "room";
  } catch {
    return "room";
  }
};
const fromPrefs = (prefs) => Object.fromEntries(PREF_KEYS.filter((k) => prefs?.[k] !== undefined).map((k) => [k, prefs[k]]));
const prefsOf = (f, place) => ({ provider: f.provider, reasoning_effort: f.reasoning_effort || "default", chime: !!f.chime, flag_interval_min: Number(f.flag_interval_min) || 10, self_name: f.self_name || "", place });

const Section = ({ n, title, hint, children }) => (
  <section className="grid lg:grid-cols-[220px_1fr] gap-4 lg:gap-10 py-8 border-b border-[var(--o-border)]">
    <div>
      <div className="font-mono text-xs text-[var(--o-muted3)]">{n}</div>
      <h2 className="font-display text-base md:text-lg font-bold mt-1">{title}</h2>
      {hint && <p className="text-xs text-[var(--o-muted2)] mt-2 leading-relaxed">{hint}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

// The last few meetings this person ran: one tap starts the next one the same way.
const RecentStrip = ({ meetings, onAgain }) => {
  if (!meetings.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4 text-xs" data-testid="recent-meetings">
      <span className={labelCls}>Start again</span>
      {meetings.map((m) => (
        <button key={m.id} type="button" onClick={() => onAgain(m.id)} className="flex items-center gap-1.5 max-w-[280px] border border-dashed border-[var(--o-border)] px-2.5 py-1 text-[var(--o-muted)] hover:text-[var(--o-fg)] hover:border-[var(--o-border-strong)] transition-colors" title={`Same title, focus, people and background as “${m.title}”`} data-testid={`recent-again-${m.id}`}>
          <ArrowCounterClockwise size={12} /> <span className="truncate">{m.title}</span> <span className="font-mono text-[10px] text-[var(--o-muted3)] whitespace-nowrap">{fmtDate(m.created_at)}</span>
        </button>
      ))}
      <Link to="/meetings" className="font-mono text-[11px] text-[var(--o-muted2)] hover:text-[var(--o-fg)] underline" data-testid="recent-all-link">All meetings</Link>
    </div>
  );
};

export default function Setup() {
  const nav = useNavigate();
  const user = useUser();
  const draftKey = `observer.setupDraft.${user.id}`;
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState(() => ({ ...EMPTY, ...fromPrefs(user.prefs), ...readDraft(draftKey), private: false }));
  const [place, setPlace] = useState(() => user.prefs?.place || readLegacyPlace());
  const [idleMinutes, setIdleMinutes] = useState(30);
  const [mode, setMode] = useState("live");
  const [file, setFile] = useState(null);
  const [speed, setSpeed] = useState("fast");
  const [busy, setBusy] = useState(false);
  const [prep, setPrep] = useState({ status: "idle" });
  const [uploadPct, setUploadPct] = useState(null);
  const [providers, setProviders] = useState(null);
  const [defaultEffort, setDefaultEffort] = useState("default");
  const [recent, setRecent] = useState([]);

  const onPick = async (picked) => {
    setFile(picked);
    setPrep({ status: "idle" });
    if (!picked || !needsExtraction(picked)) return;
    const t0 = Date.now();
    setPrep({ status: "extracting", progress: 0, originalName: picked.name });
    try {
      const audio = await extractAudio(picked, (p) => setPrep((s) => ({ ...s, progress: p })));
      audio.originalName = picked.name;
      setFile(audio);
      setPrep({ status: "done", originalName: picked.name, originalSize: picked.size, size: audio.size, seconds: (Date.now() - t0) / 1000 });
    } catch (err) {
      setPrep({ status: "failed", originalName: picked.name, error: err.message });
    }
  };
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/options").then(({ data }) => {
      if (!localStorage.getItem(draftKey) && user.prefs?.flag_interval_min === undefined) setF((p) => ({ ...p, flag_interval_min: data.settings.default_flag_interval }));
      setProviders(data.providers);
      setIdleMinutes(Number(data.settings.private_idle_minutes) || 30);
      setDefaultEffort(data.settings.reasoning_effort || "default");
    }).catch(() => {});
    api.get("/sessions").then(({ data }) => setRecent(data.filter((s) => s.owner === user.id).slice(0, 12))).catch(() => {});
  }, [draftKey, user.id, user.prefs?.flag_interval_min]);
  // The draft keeps the briefing in this browser, per account. A private meeting's must not linger, so it is removed while Private is on.
  useEffect(() => {
    if (f.private) localStorage.removeItem(draftKey);
    else localStorage.setItem(draftKey, JSON.stringify(f));
  }, [f, draftKey]);

  // Behaviour choices go to the account a moment after they change, so the next device starts the same way.
  const savedPrefs = useRef(JSON.stringify(prefsOf({ ...EMPTY, ...fromPrefs(user.prefs) }, user.prefs?.place || "room")));
  useEffect(() => {
    const next = prefsOf(f, place);
    if (JSON.stringify(next) === savedPrefs.current) return undefined;
    const t = setTimeout(() => {
      savedPrefs.current = JSON.stringify(next);
      api.put("/account/prefs", { prefs: next }).then(({ data }) => user.setUser(data.user)).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [f.provider, f.reasoning_effort, f.chime, f.flag_interval_min, f.self_name, place]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start again: the same title, focus, people, background and behaviour as an earlier meeting.
  const startAgain = useCallback(async (sid) => {
    try {
      const { data } = await api.get(`/sessions/${sid}/setup`);
      setF((p) => ({
        ...p, title: data.title, purpose: data.purpose, briefing_csv: data.briefing_csv,
        participants: data.participants.length ? data.participants.map((x) => ({ name: "", role: "", owns: "", ...x })) : EMPTY.participants,
        ...(data.provider ? { provider: data.provider } : {}), ...(data.reasoning_effort ? { reasoning_effort: data.reasoning_effort } : {}),
        ...(data.chime != null ? { chime: data.chime } : {}), ...(data.flag_interval_min ? { flag_interval_min: data.flag_interval_min } : {}),
        ...(data.self_name && data.self_name !== "Me" ? { self_name: data.self_name } : {}),
      }));
      if (data.place === "online" || data.place === "room") setPlace(data.place);
      toast.success(`Set up like “${data.title}”. Change anything, then start.`);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);
  const again = params.get("again");
  useEffect(() => {
    if (!again) return;
    startAgain(again);
    setParams({}, { replace: true });
  }, [again, startAgain, setParams]);

  // Focuses this person wrote for earlier meetings, as one-tap chips (the standard situations are chips already).
  const standard = new Set(SITUATIONS.map(([, t]) => t));
  const recentFocus = [];
  for (const m of recent) {
    const text = (m.purpose || "").trim();
    if (!text || standard.has(text) || recentFocus.some((r) => r.text === text)) continue;
    // Labelled by the meeting's title; a repeated title would make two chips look the same, so the second shows its text.
    const label = recentFocus.some((r) => r.label === m.title) ? `${text.slice(0, 42)}${text.length > 42 ? "…" : ""}` : m.title;
    recentFocus.push({ label, text });
    if (recentFocus.length === 4) break;
  }
  // The strip shows one button per distinct title: the latest meeting under each.
  const recentMeetings = recent.filter((m, i) => recent.findIndex((x) => x.title === m.title) === i).slice(0, 3);

  // A saved draft may point at a provider that has since lost its key, or an effort the model no longer supports.
  const cap = providers?.[f.provider];
  const effortLevels = cap?.effort_levels || ["default"];
  useEffect(() => {
    if (!providers) return;
    if (!providers[f.provider]?.configured) {
      const fallback = Object.keys(providers).find((k) => providers[k]?.configured && providers[k]?.effort_levels);
      if (fallback && fallback !== f.provider) setF((p) => ({ ...p, provider: fallback }));
      return;
    }
    if (!f.reasoning_effort || !effortLevels.includes(f.reasoning_effort)) {
      const next = effortLevels.includes(defaultEffort) ? defaultEffort : "default";
      if (next !== f.reasoning_effort) setF((p) => ({ ...p, reasoning_effort: next }));
    }
  }, [providers, f.provider, f.reasoning_effort, defaultEffort]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give the meeting a title");
    if (mode === "replay" && !file) return toast.error("Choose a recording to replay");
    if (prep.status === "extracting") return toast.error("Audio is still being extracted");
    let meeting = null;
    if (mode === "online") {
      try {
        meeting = await requestMeetingAudio(); // must run inside the click, before any other await
      } catch (err) {
        return toast.error(err.message);
      }
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("payload", JSON.stringify({
      ...f, flag_interval_min: Number(f.flag_interval_min), replay_speed: speed, recording_name: file?.originalName || file?.name,
      audio_source: mode === "online" ? "online" : "room",
      private: f.private && mode !== "replay",
    }));
    if (mode === "replay") fd.append("recording", file);
    try {
      const { data } = await api.post("/sessions", fd, {
        timeout: 0,
        onUploadProgress: (e) => mode === "replay" && e.total && setUploadPct(e.loaded / e.total),
      });
      if (meeting) setPendingMeetingAudio(data.id, meeting);
      nav(`/session/${data.id}`);
    } catch (err) {
      stopStream(meeting);
      toast.error(`${errMsg(err)} — check the connection and press Start again`);
      setBusy(false);
      setUploadPct(null);
    }
  };

  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="setup-page">
      <AppHeader />
      <form onSubmit={start} className="relative z-10 max-w-6xl px-6 sm:px-10 pb-32">
        <div className="pt-14 pb-6 rise">
          <p className={labelCls}>New session</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3">Brief the Observer.</h1>
          <p className="text-sm sm:text-base text-[var(--o-muted)] mt-4 max-w-2xl">Tell it what to help with. That is all it needs; you can change it during the meeting.</p>
        </div>
        <InstallBanner />
        <QuickStart
          defaults={{ provider: f.provider, reasoning_effort: f.reasoning_effort, chime: f.chime, flag_interval_min: Number(f.flag_interval_min), self_name: f.self_name || "" }}
          privateMode={f.private} onPrivateChange={set("private")} idleMinutes={idleMinutes}
          focus={f.purpose} onFocusChange={set("purpose")} recentFocus={recentFocus}
          place={place} onPlace={setPlace} title={f.title} onTitle={set("title")}
        />
        <RecentStrip meetings={recentMeetings} onAgain={startAgain} />
        <div className="flex items-center gap-4 pt-10">
          <span className={labelCls}>Or add context · all optional</span>
          <div className="flex-1 border-t border-[var(--o-border)]" />
        </div>
        <BriefingLibrary form={{ title: f.title, purpose: f.purpose, participants: f.participants, briefing_csv: f.briefing_csv }} onLoad={(v) => setF((p) => ({ ...p, ...v }))} saveDisabled={f.private && mode !== "replay"} />

        <Section n="01" title="Meeting" hint="A title, and what the Observer should help with. The same box as above.">
          <input autoComplete="off" className={inputCls} placeholder="Pricing review" value={f.title} onChange={(e) => set("title")(e.target.value)} data-testid="setup-title-input" />
          <FocusField value={f.purpose} onChange={set("purpose")} rows={3} testid="setup-purpose" />
        </Section>

        <Section n="02" title="Participants" hint="Optional. Names help the transcript say who spoke; roles help the Observer. No personality traits or history between people.">
          <ParticipantsEditor value={f.participants} onChange={set("participants")} />
        </Section>

        <Section n="03" title="Background" hint="Optional. Notes, figures or a table export (goals, owners, targets). Used to ground points, never as an agenda.">
          <CsvInput value={f.briefing_csv} onChange={set("briefing_csv")} />
        </Section>

        <Section n="04" title="Audio source" hint="Replay a past recording through the same pipeline to tune prompts and thresholds before the real meeting.">
          <SourcePicker mode={mode} setMode={setMode} file={file} onPick={onPick} prep={prep} speed={speed} setSpeed={setSpeed} selfName={f.self_name} setSelfName={set("self_name")} />
        </Section>

        <Section n="05" title="Behaviour" hint="All of these are editable mid-session from the top bar. Your choices here are remembered on your account.">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <label className="space-y-2 block">
              <span className={labelCls}>Flag interval (min)</span>
              <input type="number" min="0.5" step="0.5" className={`${inputCls} font-mono`} value={f.flag_interval_min} onChange={(e) => set("flag_interval_min")(e.target.value)} data-testid="setup-flag-interval-input" />
            </label>
            <label className="space-y-2 block">
              <span className={labelCls}>Reasoning provider</span>
              <select className={`${inputCls} font-mono`} value={f.provider} onChange={(e) => set("provider")(e.target.value)} data-testid="setup-provider-select">
                <option value="anthropic">Anthropic · Sonnet</option>
                <option value="fireworks" disabled={providers ? !providers.fireworks?.configured : false}>
                  Fireworks{providers && !providers.fireworks?.configured ? " · no API key" : ""}
                </option>
              </select>
              {providers && !providers.fireworks?.configured && (
                <span className="block text-[11px] text-[var(--o-muted3)]" data-testid="setup-fireworks-unavailable">Fireworks needs its API key in Admin before it can be used.</span>
              )}
            </label>
            <label className="space-y-2 block">
              <span className={labelCls}>Thinking effort</span>
              <select className={`${inputCls} font-mono`} value={f.reasoning_effort || "default"} onChange={(e) => set("reasoning_effort")(e.target.value)} disabled={effortLevels.length < 2} data-testid="setup-effort-select">
                {effortLevels.map((lvl) => <option key={lvl} value={lvl}>{lvl === "default" ? "Model default" : lvl[0].toUpperCase() + lvl.slice(1)}</option>)}
              </select>
              {cap?.effort_note && <span className="block text-[11px] text-[var(--o-muted3)]">{cap.effort_note}</span>}
            </label>
            <div className="space-y-2">
              <span className={labelCls}>Chime on flag</span>
              <div className="flex items-center gap-3 h-[42px]">
                <Switch checked={f.chime} onCheckedChange={set("chime")} data-testid="setup-chime-switch" />
                <span className="text-sm text-[var(--o-muted)]">{f.chime ? "On" : "Off"}</span>
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--o-border-soft)] pt-5 mt-1 space-y-3" data-testid="setup-private-row">
            {mode === "replay" ? (
              <p className="text-xs text-[var(--o-muted2)]">Replays are always saved, so you can compare runs. Private applies to live meetings.</p>
            ) : (
              <>
                <PrivateToggle value={f.private} onChange={set("private")} testid="setup-private-toggle" />
                <PrivateNote idleMinutes={idleMinutes} full />
              </>
            )}
          </div>
        </Section>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-8">
          <p className="text-xs text-[var(--o-muted2)] max-w-md">
            Say it aloud at the start: Observer is listening, what it does, and that anyone can ask for it to be paused. <Link to="/privacy" className="underline hover:text-[var(--o-fg)]" data-testid="setup-privacy-link">What to say, and what it keeps</Link>.
          </p>
          <button type="submit" disabled={busy} className={`${btnPrimary} px-8 py-3.5`} data-testid="setup-start-button">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : null}
            {uploadPct != null ? `Uploading ${Math.round(uploadPct * 100)}%` : mode === "replay" ? "Start replay" : mode === "online" ? "Share meeting audio & start" : "Start listening"} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </form>
    </div>
  );
}
